import { useRef, useState, useEffect } from "react";

/**
 * Detects whether the text inside an element is truncated (e.g. cut off with
 * a CSS ellipsis) by comparing its scroll size against its client size.
 *
 * Re-checks on mount and whenever the element resizes (via ResizeObserver),
 * which also covers layout changes such as font loading or column reflow.
 *
 * @returns {[React.RefObject, boolean]} ref to attach to the element and the
 *   current truncated state.
 */
const useIsTruncated = () => {
  const elementRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) {
      return;
    }

    const checkTruncation = () => {
      setIsTruncated(
        element.scrollWidth > element.clientWidth ||
          element.scrollHeight > element.clientHeight
      );
    };

    checkTruncation();

    const observer = new ResizeObserver(checkTruncation);
    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, []);

  return [elementRef, isTruncated];
};

export default useIsTruncated;
